import type { CallbackType, ConfigResponse } from '../store/modules/merchant/types';

/** Config names that never inherit to child merchants (aligned with MMS NON_INHERITABLE_CONFIGS). */
export const NON_INHERITABLE_CONFIG_NAMES: readonly ConfigResponse['name'][] = [
  'CLICK_TO_PAY',
  'SURCHARGE_FEE',
  'GENERAL_FEE',
];

export function isInheritableConfigName(name: ConfigResponse['name']): boolean {
  return !NON_INHERITABLE_CONFIG_NAMES.includes(name);
}

/** Portal-facing labels for save bar / banner copy; align with section titles on each settings page. */
const CONFIG_DISPLAY_NAMES: Partial<Record<ConfigResponse['name'], string>> = {
  ADDITIONAL_TRANSACTION_DATA: 'Additional Transaction Data',
  AGENTIC_CHECKOUT: 'Agentic Checkout',
  AVS_DECLINE: 'AVS Decline',
  CO_BRANDING: 'Co-branding',
  DISALLOW_CART_EDITS: 'Cart edits',
  DISCOUNTS: 'Discounts',
  FLEX_PAY: 'Flex Pay',
  GENERAL_FEE: 'General fee',
  IS_PAYMENT_PLATFORM: 'Payment Platform',
  MERCHANT_BRANDING: 'Branding',
  NON_ACCEPTED_CARD_BRANDS: 'Card Acceptance',
  NO_SHIPPING_REQUIRED: 'Shipping required',
  PARTIAL_AUTHORIZATIONS: 'Partial Authorization',
  PARTIAL_PAYMENTS: 'Partial Payments',
  PAYLINKS_RECURRING_CHECKBOX: 'Paylinks — save cards for future transactions',
  SETTLEMENT_TYPE: 'Payment Capture',
  SHOPPER_CREDENTIAL_ACCESS: 'Payment Credential Sharing',
  SPLIT_PAYMENTS: 'Split Payments',
  SURCHARGE_FEE: 'Surcharge fee',
  SUPPORTED_COUNTRIES: 'Cross Border Payments',
  TERMS_LINKS: 'Shopper consent links',
  TIPPING_PERCENTAGES: 'Tipping',
};

const CALLBACK_DISPLAY_NAMES: Record<CallbackType, string> = {
  Products: 'Products callback',
  ShippingAndTax: 'Shipping and tax callback',
  Fulfillability: 'Fulfillability callback',
  ShopperCredentialSharing: 'Payment Credential Sharing',
  AgenticCheckoutValidateTotal: 'Validate total callback',
};

function formatSettingNameFallback(name: string): string {
  return name
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function getConfigDisplayLabel(name: ConfigResponse['name']): string {
  return CONFIG_DISPLAY_NAMES[name] ?? formatSettingNameFallback(name);
}

export function getCallbackDisplayLabel(callbackType: CallbackType): string {
  return CALLBACK_DISPLAY_NAMES[callbackType] ?? formatSettingNameFallback(callbackType);
}

export function uniqueDisplayLabels(labels: string[]): string[] {
  return labels.filter((label, index) => labels.indexOf(label) === index);
}

function formatLabelsList(labels: string[]): string {
  if (labels.length === 1) {
    return labels[0];
  }
  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`;
  }
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

export function inheritableLabelsFromConfigNames(names: ConfigResponse['name'][]): string[] {
  return uniqueDisplayLabels(
    names.filter(isInheritableConfigName).map((name) => getConfigDisplayLabel(name)),
  );
}

export type ParentHierarchySaveBarCopy = {
  text: string;
  detailText?: string;
};

/** Page-level info banner for merchants with descendants (config/settings pages). */
export const PARENT_HIERARCHY_SETTINGS_BANNER_MESSAGE =
  "Some settings on this page also apply to child merchants. When you save, those child merchants inherit these changes unless they've set their own value.";

/** Webhooks: hierarchy impact is per subscription via the associated-merchants toggle. */
export const PARENT_HIERARCHY_WEBHOOKS_BANNER_MESSAGE =
  'When you add or edit a subscription, you can enable receiving events from all associated merchants. That applies to child merchants in your hierarchy only for subscriptions where that option is turned on.';

/** Merchant domains: whitelisted URLs are managed per merchant, with hierarchy merge. */
export const PARENT_HIERARCHY_DOMAINS_BANNER_MESSAGE =
  'When you add, edit, or remove merchant domains on this account, child merchants in your hierarchy may use the same approved domains. Child merchants can also maintain their own domain lists.';

/** Save bar primary + detail copy for parent merchants with inheritable pending changes. */
export function getParentHierarchySaveBarCopy(
  inheritableSettingLabels: string[],
  options: { isSaving: boolean; hasDescendants: boolean },
): ParentHierarchySaveBarCopy {
  if (options.isSaving) {
    return { text: 'Saving current changes...' };
  }

  if (!options.hasDescendants || inheritableSettingLabels.length === 0) {
    return { text: 'Save current changes?' };
  }

  const multiple = inheritableSettingLabels.length > 1;
  const detailText = multiple
    ? `The changes to ${formatLabelsList(inheritableSettingLabels)} may apply to child merchants.`
    : `Changes to the ${inheritableSettingLabels[0]} setting may apply to child merchants.`;

  return {
    text: 'Save current changes?',
    detailText,
  };
}

/** Save bar detail when a child merchant overrides an inherited inheritable setting. */
export function getChildOverrideSaveBarDetailText(
  inheritableSettingLabels: string[],
  options: { hasDescendants: boolean },
): string {
  if (!options.hasDescendants || inheritableSettingLabels.length === 0) {
    return 'Saving overrides the inherited setting for this merchant only.';
  }

  const multiple = inheritableSettingLabels.length > 1;
  const childHierarchyCopy = multiple
    ? `Changes to ${formatLabelsList(inheritableSettingLabels)} may also apply to child merchants unless they've set their own value.`
    : `Changes to the ${inheritableSettingLabels[0]} setting may also apply to child merchants unless they've set their own value.`;

  return `Saving overrides the inherited setting for this merchant. ${childHierarchyCopy}`;
}

/** Save bar detail on multi-setting pages while customizing, before anything is edited. */
export function getChildCustomizeIdleSaveBarDetailText(hasDescendants: boolean): string {
  if (hasDescendants) {
    return (
      'Change a setting above, then save to override the inherited value for this merchant. ' +
      "Your changes may also apply to child merchants unless they've set their own value."
    );
  }
  return 'Change a setting above, then save to override the inherited value for this merchant only.';
}

/** Info banner when a child merchant views an inherited setting (Customize flow). */
export function getChildInheritedInfoBannerCopy(hasDescendants: boolean): string {
  if (hasDescendants) {
    return (
      "This setting uses your parent merchant's value. Choose Customize to set your own value for this merchant. " +
      "When you save, child merchants may inherit your change unless they've set their own value."
    );
  }
  return "This setting uses your parent merchant's value. Choose Customize to change it for this merchant only.";
}

/** Inline warning while a child merchant is customizing an inherited setting. */
export function getChildCustomizeInheritedWarningCopy(hasDescendants: boolean): string {
  if (hasDescendants) {
    return (
      "You're overriding an inherited parent value for this merchant. " +
      "When you save, child merchants may inherit your change unless they've set their own value."
    );
  }
  return "You're setting a value for this merchant only. Saving overrides the inherited setting for this merchant; other merchants in your hierarchy are not changed.";
}
